import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
} from "@mui/material";
import APIAxios from "../../Api/APIAxios";

const URL_Program = "/programas/usados";

const UserTabelaProgram = ({ diaProdHora, user }) => {

    const apiAxios = APIAxios();

    const [program, setProgram] = useState([]);
    const [progLoading, setProgLoading] = useState(false);
    const [progErro, setProgErro] = useState("");
    const [totalScore, setTotalScore] = useState(0);

    const loadProgram = async () => {
        setProgLoading(true);
        setProgErro("");

        try {
            const response = await apiAxios.post(URL_Program, {
                data_inicio: diaProdHora,
                data_fim: diaProdHora,
                id_usuario: user.idColaborador,
                computador: "*"
            });

            const rawData = Array.isArray(response.data.data)
                ? response.data.data
                : JSON.parse(response.data.data || "[]");

            const total = rawData.reduce((acc, row) => acc + row.score, 0);
            setTotalScore(total);
            setProgram(rawData);

        } catch (err) {
            if (!err.response) {
                setProgErro("Servidor não responde");
            } else if (err.response.status === 400 || err.response.status === 401) {
                setProgErro(err.response.data.erro);
            } else {
                setProgErro("Erro desconhecido.");
            }
        } finally {
            setProgLoading(false);
        }
    };

    useEffect(() => {
        if (diaProdHora) {
            loadProgram();
        }
    }, [diaProdHora]);

    const filteredPrograms = program
        .filter(prg => Math.round((prg.score * 100) / totalScore) > 2)
        .sort((a, b) => b.score - a.score);

    const formatTime = (minutes) => {
        const h = Math.floor(minutes / 60);
        const m = String(minutes % 60).padStart(2, "0");
        return `${h}h${m}`;
    };

    const buildChartData = () => {
        if (!diaProdHora || filteredPrograms.length === 0)
            return { minTs: null, maxTs: null, segmentsPerProgram: {} };

        const [dd, MM, yyyy] = diaProdHora.split("/");
        const baseDate = new Date(parseInt(yyyy), parseInt(MM) - 1, parseInt(dd));

        const toTs = (timeStr) => {
            const [h, m] = timeStr.split(":").map(Number);
            return baseDate.getTime() + (h * 60 + m) * 60000;
        };

        const toMin = (timeStr) => {
            const [h, m] = timeStr.split(":").map(Number);
            return h * 60 + m;
        };

        const segmentsPerProgram = {};
        let minTs = Infinity;
        let maxTs = -Infinity;

        filteredPrograms.forEach(prg => {
            if (!prg.minutos || prg.minutos.length === 0) return;

            const sorted = [...prg.minutos].sort();
            minTs = Math.min(minTs, toTs(sorted[0]));
            maxTs = Math.max(maxTs, toTs(sorted[sorted.length - 1]) + 60000);

            let segStart = sorted[0];
            let segEnd = sorted[0];
            const segments = [];

            for (let i = 1; i < sorted.length; i++) {
                const gap = toMin(sorted[i]) - toMin(sorted[i - 1]);

                if (gap <= 1) {
                    segEnd = sorted[i];
                } else {
                    segments.push([toTs(segStart), toTs(segEnd) + 60000]);
                    segStart = sorted[i];
                    segEnd = sorted[i];
                }
            }
            segments.push([toTs(segStart), toTs(segEnd) + 60000]);
            segmentsPerProgram[prg.body] = segments;
        });

        const firstActivity = minTs === Infinity ? null : minTs;

        return {
            minTs: firstActivity ? firstActivity - 30 * 60000 : null,
            maxTs: maxTs === -Infinity ? null : maxTs,
            segmentsPerProgram,
        };
    };

    const { minTs, maxTs, segmentsPerProgram } = buildChartData();

    const totalRange = minTs && maxTs ? maxTs - minTs : 0;

    const hourTicks = (() => {
        if (!minTs || !maxTs) return [];
        const ticks = [];
        const start = new Date(minTs);
        start.setMinutes(0, 0, 0);
        start.setHours(start.getHours() + 1);
        for (let t = start.getTime(); t <= maxTs; t += 3600000) {
            ticks.push(t);
        }
        return ticks;
    })();

    const barBg = (() => {
        if (!totalRange || hourTicks.length === 0) return "#e0e7ff";
        const stops = hourTicks.flatMap(tick => {
            const pct = ((tick - minTs) / totalRange) * 100;
            return [
                `#e0e7ff ${(pct - 0.25).toFixed(2)}%`,
                `#c7d2fe ${(pct - 0.25).toFixed(2)}%`,
                `#c7d2fe ${(pct + 0.25).toFixed(2)}%`,
                `#e0e7ff ${(pct + 0.25).toFixed(2)}%`,
            ];
        });
        return `linear-gradient(to right, #e0e7ff, ${stops.join(", ")}, #e0e7ff)`;
    })();

    const renderMiniBar = (prg) => {
        const segments = segmentsPerProgram[prg.body];
        return (
            <Box sx={{
                flex: 1,
                height: 14,
                background: barBg,
                borderRadius: "4px",
                position: "relative",
                overflow: "hidden",
                mx: 1.5,
            }}>
                {segments && totalRange > 0 && segments.map(([start, end], i) => (
                    <Box key={i} sx={{
                        position: "absolute",
                        left: `${Math.max(0, ((start - minTs) / totalRange) * 100)}%`,
                        width: `${Math.max(0.5, ((end - start) / totalRange) * 100)}%`,
                        height: "100%",
                        background: "#647DEE",
                        borderRadius: "2px",
                    }} />
                ))}
            </Box>
        );
    };

    return (
        <Box sx={{ width: "100%", mt: 2, position: "relative" }}>
            <Card sx={{
                width: "100%",
                boxShadow: "0 8px 28px rgba(26, 56, 101, 0.12), 0 2px 6px rgba(100,125,238,0.08)",
                borderRadius: "16px",
                border: "1px solid rgba(100, 125, 238, 0.15)",
                overflow: "hidden",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0, left: 0, right: 0,
                    height: "4px",
                    background: "linear-gradient(90deg, #269FFB, #647DEE, #1A3865)",
                },
            }}>
                <CardContent sx={{ pt: 3 }}>
                    <Typography gutterBottom align="center" sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: "#1A3865",
                        letterSpacing: "0.4px",
                    }}>
                        Programas Utilizados em {diaProdHora}
                    </Typography>

                    <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mb: 2, flexWrap: "wrap" }}>
                        {[
                            { color: "#647DEE", label: "Ativo — programa em uso" },
                            { color: "#e0e7ff", label: "Sem uso — outro programa ou sem registro", border: "1px solid #c7d2fe" },
                        ].map(({ color, label, border }) => (
                            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                <Box sx={{ width: 14, height: 14, borderRadius: "3px", background: color, border: border || "none", flexShrink: 0 }} />
                                <Typography sx={{ fontSize: "0.75rem", color: "#6b7a99" }}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {!progLoading && !progErro && filteredPrograms.length > 0 && (
                        <Box sx={{ mb: 2, px: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", py: 0.75, borderBottom: "2px solid #e0e7ff", mb: 0.25 }}>
                                <Box sx={{ width: 24, flexShrink: 0 }} />
                                <Typography sx={{ width: 200, fontSize: "0.68rem", fontWeight: 700, color: "#6b7a99", textTransform: "uppercase", letterSpacing: "0.5px", flexShrink: 0 }}>
                                    Programa / Arquivo
                                </Typography>
                                <Box sx={{ flex: 1, mx: 1.5 }} />
                                <Typography sx={{ width: 65, fontSize: "0.68rem", fontWeight: 700, color: "#6b7a99", textAlign: "right", flexShrink: 0 }}>
                                    Tempo Total
                                </Typography>
                                <Typography sx={{ width: 42, fontSize: "0.68rem", fontWeight: 700, color: "#6b7a99", textAlign: "right", flexShrink: 0 }}>
                                    %
                                </Typography>
                            </Box>

                            {[...filteredPrograms].sort((a, b) => b.score - a.score).map((prg, idx) => {
                                const pct = Math.round((prg.score * 100) / totalScore);
                                return (
                                    <Box key={prg.body} sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        py: 0.75,
                                        borderBottom: "1px solid #f0f2ff",
                                        "&:last-child": { borderBottom: "none" },
                                    }}>
                                        <Typography sx={{ width: 20, fontSize: "0.7rem", fontWeight: 700, color: "#647DEE", flexShrink: 0, mr: 0.5 }}>
                                            {idx + 1}
                                        </Typography>
                                        <Typography sx={{
                                            width: 200,
                                            fontSize: "0.78rem",
                                            fontWeight: 600,
                                            color: "#1A3865",
                                            flexShrink: 0,
                                            lineHeight: 1.35,
                                            wordBreak: "break-word",
                                        }}>
                                            {prg.body}
                                        </Typography>
                                        {renderMiniBar(prg)}
                                        <Typography sx={{ width: 65, fontSize: "0.78rem", fontWeight: 700, color: "#1A3865", textAlign: "right", flexShrink: 0 }}>
                                            {formatTime(prg.score)}
                                        </Typography>
                                        <Typography sx={{ width: 42, fontSize: "0.78rem", fontWeight: 700, color: "#647DEE", textAlign: "right", flexShrink: 0 }}>
                                            {pct}%
                                        </Typography>
                                    </Box>
                                );
                            })}
                            {/* Eixo de horários */}
                            {minTs && maxTs && hourTicks.length > 0 && (
                                <Box sx={{ display: "flex", alignItems: "flex-start", pt: 0.5, mt: 0.5, borderTop: "1px dashed #c7d2fe" }}>
                                    <Box sx={{ width: 24, flexShrink: 0 }} />
                                    <Box sx={{ width: 200, flexShrink: 0 }} />
                                    <Box sx={{ flex: 1, mx: 1.5, position: "relative", height: 22 }}>
                                        {hourTicks.map(tick => {
                                            const leftPct = ((tick - minTs) / totalRange) * 100;
                                            return (
                                                <Box key={tick} sx={{ position: "absolute", left: `${leftPct}%`, transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                    <Box sx={{ width: "1px", height: 4, background: "#c7d2fe" }} />
                                                    <Typography sx={{ fontSize: "0.6rem", color: "#6b7a99", whiteSpace: "nowrap", mt: "1px" }}>
                                                        {`${new Date(tick).getHours()}:00`}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    <Box sx={{ width: 65, flexShrink: 0 }} />
                                    <Box sx={{ width: 42, flexShrink: 0 }} />
                                </Box>
                            )}
                        </Box>
                    )}

                    {progLoading && (
                        <CircularProgress
                            size={35}
                            sx={{
                                color: "#647DEE",
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 1,
                            }}
                        />
                    )}

                    {progErro && (
                        <Typography align="center" sx={{ color: "#f44336", fontSize: "0.9rem", py: 2 }}>
                            {progErro}
                        </Typography>
                    )}

                    {!progLoading && !progErro && filteredPrograms.length === 0 && program.length > 0 && (
                        <Typography align="center" sx={{ color: "#6b7a99", fontSize: "0.9rem", py: 4 }}>
                            Nenhum programa com uso expressivo registrado para este dia.
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

UserTabelaProgram.propTypes = {
    diaProdHora: PropTypes.string,
    user: PropTypes.object,
};

export default UserTabelaProgram;
